import {
  type Filter,
  FilterId,
  FilterType,
  type PrimaryFilterId,
  type TreeSelection,
} from '../../filters/types/filters';
import { isFlatTreemapSegment } from '../../filters/utils/treemapDrilldownLevels';
import { getAdaptiveChartType } from '../../hooks/useAdaptiveChartType';
import { getResolvedDataSetType } from '../Chart/ChartRoot/can-draw-resolved-data';
import { isSpecialChartType } from '../Chart/ChartRoot/classify-chart-data-route';
import { getIcicleSegment } from '../Chart/Icicle/icicle-segment';
import { TOP_COMPANIES_MAX } from '../DataProvider/constants';
import {
  getTopNEntityLimit,
  getTopNFilterValue,
} from '../DataProvider/TopNDataProvider';
import type { TableDimension } from '../DataProvider/types';
import type { PrimaryEntity } from '../PageConfigProvider/types/chart';
import {
  isShareOfSegmentPlot,
  type PlotOptions,
  type PositionedItem,
  type TableColumnConfig,
} from '../PageConfigProvider/types/item';
import type { MetricValues } from '../PageConfigProvider/types/metric';
import {
  dimensionColumn,
  getPlotTableColumns,
  variableColumn,
} from '../PageConfigProvider/utils/plot-table-columns';
import { isSingleEntityChart } from '../PageState/utils/is-single-entity-chart';
import {
  compositionInputsOf,
  getHandlebarsCompositionMetrics,
  isComputedFromComposition,
} from './composition-inputs';
import {
  type CompositionV4TableSource,
  getCompositionV4BreakdownNames,
  getCompositionV4TableSource,
  MAX_COMPOSITION_V4_BREAKDOWNS,
} from './composition-v4-tables';
import { dimensionFromPrimaryFilter } from './convert-composition-v4-data';
import {
  dimensionOfLevel,
  getBreakdownLevel,
  getBreakdownLevelPair,
  isSublevelOf,
  taxonomyOf,
} from './taxonomy-levels';

type PlannedTable = {
  key: string;
  dimensions: TableDimension[];
  metrics: MetricValues[];
  topN?: number;
};

type PlanContext = {
  primaryFilterId: PrimaryFilterId;
  entityCount: number;
  filters: readonly Filter[];
  geographySelectionListIds: readonly string[];
  primaryEntities: Readonly<Record<string, PrimaryEntity>>;
};

export type TopCompaniesOf = {
  primaryDimension: PrimaryFilterId;
  entity: Pick<TreeSelection, 'id' | 'levelId'>;
  limit: number;
};

export type PlannedQuery = {
  key: string;
  primaryDimension: PrimaryFilterId;
  tables: Readonly<Record<string, PlannedTable>>;
  topCompaniesOf?: TopCompaniesOf;
};

export type PlannedItem = {
  queryKey: string;
  tableKey: string;
  metrics: MetricValues[];
  entityId?: string;
};

export type DataResolverPlan = {
  queries: Readonly<Record<string, PlannedQuery>>;
  items: Readonly<Record<string, PlannedItem>>;
};

export const EMPTY_PLAN: DataResolverPlan = { queries: {}, items: {} };

const shareOfSegmentColumns = (
  { secondaryDimension }: PlotOptions,
  primaryFilterId: PrimaryFilterId
): TableColumnConfig[] => {
  if (!secondaryDimension) return [];
  return [
    dimensionColumn(dimensionFromPrimaryFilter(primaryFilterId)),
    dimensionColumn(secondaryDimension),
    dimensionColumn('timeseries'),
    variableColumn('headcount'),
    variableColumn('share_of_headcount'),
  ];
};

const icicleColumns = (
  { secondaryDimension }: PlotOptions,
  primaryFilterId: PrimaryFilterId
): TableColumnConfig[] => {
  if (secondaryDimension === 'subsidiaries') return [];
  const segment = getIcicleSegment(primaryFilterId, secondaryDimension);
  return [
    dimensionColumn(dimensionFromPrimaryFilter(primaryFilterId)),
    dimensionColumn(segment, `level:${segment}`),
    dimensionColumn(segment, `sublevel:${segment}`),
    variableColumn('headcount'),
    variableColumn('share_of_headcount'),
  ];
};

const withCompositionInputs = (
  columns: readonly TableColumnConfig[]
): TableColumnConfig[] => {
  const isComputed = (column: TableColumnConfig) =>
    column.kind === 'variable' && isComputedFromComposition(column.metric);
  const inputs = columns.flatMap((column) =>
    column.kind === 'variable' && isComputed(column)
      ? (compositionInputsOf(column.metric) ?? [])
      : []
  );
  if (inputs.length === 0) return [...columns];
  const isOverTime = columns.some(
    (column) => column.kind === 'dimension' && column.dimension === 'timeseries'
  );
  const metrics = new Set([
    ...columns.flatMap((column) =>
      column.kind === 'variable' && !isComputed(column) ? [column.metric] : []
    ),
    ...inputs.flatMap(({ metrics }) => metrics),
  ]);
  return [
    ...columns.filter((column) => column.kind === 'dimension'),
    ...(!isOverTime && inputs.some(({ overTime }) => overTime)
      ? [dimensionColumn('timeseries')]
      : []),
    ...[...metrics].map(variableColumn),
  ];
};

const handlebarsColumns = (
  item: PositionedItem,
  primaryFilterId: PrimaryFilterId
): TableColumnConfig[] => {
  const metrics = getHandlebarsCompositionMetrics(item);
  return metrics.length === 0
    ? []
    : withCompositionInputs([
        dimensionColumn(dimensionFromPrimaryFilter(primaryFilterId)),
        dimensionColumn('timeseries'),
        ...metrics.map(variableColumn),
      ]);
};

const getItemTableColumns = (
  item: PositionedItem,
  primaryFilterId: PrimaryFilterId,
  entityCount: number
): TableColumnConfig[] => {
  const { plot } = item;
  if (!plot) return [];
  if (plot.chartType === 'table') return plot.tableColumns ?? [];
  if (plot.chartType === 'icicle') return icicleColumns(plot, primaryFilterId);
  if (plot.chartType === 'gauge-v2' || plot.chartType === 'text') {
    return handlebarsColumns(item, primaryFilterId);
  }
  if (isShareOfSegmentPlot(plot)) {
    return shareOfSegmentColumns(plot, primaryFilterId);
  }
  if (
    (plot.chartType && isSpecialChartType(plot.chartType)) ||
    getResolvedDataSetType(item, plot.defaultMetricOptions?.[0]) === undefined
  ) {
    return [];
  }

  const columns = withCompositionInputs(
    getPlotTableColumns(plot, dimensionFromPrimaryFilter(primaryFilterId))
  );
  if (getAdaptiveChartType(plot, entityCount) !== 'treemap') return columns;
  if (plot.treemap?.taxonomyCoverage?.topN) return [];
  const { secondaryDimension } = plot;
  return [
    ...columns,
    ...(secondaryDimension && !isFlatTreemapSegment(secondaryDimension)
      ? [dimensionColumn(secondaryDimension, `sublevel:${secondaryDimension}`)]
      : []),
    variableColumn('share_of_headcount'),
  ];
};

const breakdownLevels = (
  item: PositionedItem,
  dimension: TableDimension,
  count: number,
  { filters, geographySelectionListIds }: PlanContext
): TableDimension[] | undefined => {
  const level = getBreakdownLevel(dimension, {
    filters,
    breakdownLevels: item.plot?.breakdownLevels,
    geographySelectionListIds,
  });
  if (!level) return [dimension];
  if (count === 1) return [level];
  return count === 2
    ? getBreakdownLevelPair(dimension, level, geographySelectionListIds)
    : undefined;
};

const levelDimension = (
  item: PositionedItem,
  dimension: TableDimension,
  count: number,
  primaryDimension: TableDimension | undefined,
  context: PlanContext
): TableDimension[] | undefined => {
  if (dimension !== primaryDimension) {
    return breakdownLevels(item, dimension, count, context);
  }
  if (
    count === 1 ||
    !taxonomyOf(dimension, context.geographySelectionListIds)
  ) {
    return [dimension];
  }
  const levels = breakdownLevels(item, dimension, count - 1, context);
  return levels && [dimension, ...levels];
};

const dimensionRank = (
  dimension: TableDimension,
  primaryDimension: TableDimension | undefined
): number => {
  if (dimension === primaryDimension) return 0;
  return dimension === 'timeseries' ? 2 : 1;
};

// The entity first, levels above their sublevels and time last, so a table
// is the same whatever order its columns are in.
const compareDimensions =
  (primaryDimension: TableDimension | undefined) =>
  (a: TableDimension, b: TableDimension): number =>
    dimensionRank(a, primaryDimension) - dimensionRank(b, primaryDimension) ||
    dimensionOfLevel(a).localeCompare(dimensionOfLevel(b)) ||
    Number(isSublevelOf(a, b)) - Number(isSublevelOf(b, a));

const tableDimensions = (
  item: PositionedItem,
  columns: readonly TableColumnConfig[],
  primaryDimension: TableDimension | undefined,
  context: PlanContext
): TableDimension[] | undefined => {
  const counts = new Map<TableDimension, number>();
  for (const column of columns) {
    if (column.kind === 'dimension') {
      counts.set(column.dimension, (counts.get(column.dimension) ?? 0) + 1);
    }
  }
  const dimensions: TableDimension[] = [];
  for (const [dimension, count] of counts) {
    const leveled = levelDimension(
      item,
      dimension,
      count,
      primaryDimension,
      context
    );
    if (!leveled) return undefined;
    dimensions.push(...leveled);
  }
  return dimensions.sort(compareDimensions(primaryDimension));
};

const tableMetrics = (
  columns: readonly TableColumnConfig[]
): MetricValues[] => [
  ...new Set(
    columns.flatMap((column) =>
      column.kind === 'variable' ? [column.metric] : []
    )
  ),
];

const mergeMetrics = (
  planned: readonly MetricValues[],
  added: readonly MetricValues[]
): MetricValues[] => [...new Set([...planned, ...added])].sort();

type PlannedItemTable = {
  query: Omit<PlannedQuery, 'tables'>;
  table: PlannedTable;
  source: CompositionV4TableSource;
};

const compositionV4QueryKey = (primaryDimension: PrimaryFilterId) =>
  `composition_v4:${primaryDimension}`;

const topCompaniesQueryKey = ({
  primaryDimension,
  entity,
  limit,
}: TopCompaniesOf) =>
  `top_companies:${primaryDimension}:${entity.levelId}:${entity.id}:${limit}`;

const getChartPrimaryEntity = (
  itemId: string,
  {
    primaryFilterId,
    filters,
    primaryEntities,
  }: Pick<PlanContext, 'primaryFilterId' | 'filters' | 'primaryEntities'>
): Pick<TreeSelection, 'id' | 'levelId'> | undefined => {
  const primaryFilter = filters.find(({ id }) => id === primaryFilterId);
  const selections =
    primaryFilter?.type === FilterType.TREE ? primaryFilter.selections : [];
  const picked = primaryEntities[itemId];
  const selection =
    (picked &&
      selections.find(
        ({ id, levelId }) =>
          levelId === picked.levelId &&
          id.split('_').pop() === String(picked.id)
      )) ??
    selections[0];
  return selection && { id: selection.id, levelId: selection.levelId };
};

const singleEntityId = (
  item: PositionedItem,
  query: Omit<PlannedQuery, 'tables'>,
  context: PlanContext
): string | undefined => {
  if (query.topCompaniesOf || !isSingleEntityChart(item)) return undefined;
  return getChartPrimaryEntity(item.id, context)?.id.split('_').pop();
};

const planTopCompaniesTable = (
  item: PositionedItem,
  table: PlannedTable,
  context: PlanContext
): PlannedItemTable | undefined => {
  const entity = getChartPrimaryEntity(item.id, context);
  const source = getCompositionV4TableSource(FilterId.COMPANY, table);
  if (!entity || !source || context.primaryFilterId === FilterId.COMPANY) {
    return undefined;
  }
  const topCompaniesOf: TopCompaniesOf = {
    primaryDimension: context.primaryFilterId,
    entity,
    limit: getTopNEntityLimit(item.plot?.plotFilters, TOP_COMPANIES_MAX),
  };
  return {
    query: {
      key: topCompaniesQueryKey(topCompaniesOf),
      primaryDimension: FilterId.COMPANY,
      topCompaniesOf,
    },
    table,
    source,
  };
};

const breakdownTopNOf = (
  item: PositionedItem,
  dimensions: readonly TableDimension[],
  primaryFilterId: PrimaryFilterId
): number | undefined => {
  const entity = dimensionFromPrimaryFilter(primaryFilterId);
  const [breakdown, ...more] = dimensions.filter((d) => d !== entity);
  return breakdown && breakdown !== 'timeseries' && more.length === 0
    ? getTopNFilterValue(item.plot?.plotFilters)
    : undefined;
};

const planItemTable = (
  item: PositionedItem,
  context: PlanContext
): PlannedItemTable | undefined => {
  if (item.plot?.plotParameters?.length) return undefined;
  const { primaryFilterId, entityCount } = context;
  const columns = getItemTableColumns(item, primaryFilterId, entityCount);
  const dimensions = tableDimensions(
    item,
    columns,
    dimensionFromPrimaryFilter(primaryFilterId),
    context
  );
  if (!dimensions) return undefined;

  const topN = breakdownTopNOf(item, dimensions, primaryFilterId);
  const table: PlannedTable = {
    key: [...dimensions, ...(topN === undefined ? [] : [`top_${topN}`])].join(
      ':'
    ),
    dimensions,
    metrics: tableMetrics(columns),
    ...(topN !== undefined && { topN }),
  };
  const source = getCompositionV4TableSource(primaryFilterId, table);
  if (!source) return planTopCompaniesTable(item, table, context);
  return {
    query: {
      key: compositionV4QueryKey(primaryFilterId),
      primaryDimension: primaryFilterId,
    },
    table,
    source,
  };
};

export const planDataQueries = ({
  items,
  primaryFilterId,
  ...context
}: Omit<PlanContext, 'primaryFilterId'> & {
  items: readonly PositionedItem[];
  primaryFilterId: PrimaryFilterId | undefined;
}): DataResolverPlan => {
  if (!primaryFilterId) return EMPTY_PLAN;

  const queries: Record<string, PlannedQuery> = {};
  const breakdownsByQuery = new Map<string, Set<string>>();
  const plannedItems: Record<string, PlannedItem> = {};
  for (const item of items) {
    const itemContext = { ...context, primaryFilterId };
    const planned = planItemTable(item, itemContext);
    if (!planned) continue;

    const { query, table, source } = planned;
    const breakdowns = breakdownsByQuery.get(query.key) ?? new Set<string>();
    const added = getCompositionV4BreakdownNames(source).filter(
      (name) => !breakdowns.has(name)
    );
    if (breakdowns.size + added.length > MAX_COMPOSITION_V4_BREAKDOWNS) {
      continue;
    }
    for (const name of added) breakdowns.add(name);
    breakdownsByQuery.set(query.key, breakdowns);

    const tables = queries[query.key]?.tables ?? {};
    const existing = tables[table.key];
    queries[query.key] = {
      ...query,
      tables: {
        ...tables,
        [table.key]: existing
          ? {
              ...existing,
              metrics: mergeMetrics(existing.metrics, table.metrics),
            }
          : table,
      },
    };
    const entityId = singleEntityId(item, query, itemContext);
    plannedItems[item.id] = {
      queryKey: query.key,
      tableKey: table.key,
      metrics: table.metrics,
      ...(entityId !== undefined && { entityId }),
    };
  }
  if (Object.keys(plannedItems).length === 0) return EMPTY_PLAN;

  return { queries, items: plannedItems };
};
